import React, { useState, useEffect } from "react";
import Menu from "./Menu";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Custom icons component to replace FontAwesome
const Icon = ({ name }) => {
  const icons = {
    faTable: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z"/>
      </svg>
    ),
  
    faChevronLeft: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
      </svg>
    ),
    faChevronRight: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
      </svg>
    ),
    faTimes: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
      </svg>
    ),
    faPlus: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
      </svg>
    ),
    faSpinner: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
        <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
      </svg>
    )
  };

  return icons[name] || null;
};

// Modern color scheme
const colors = {
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  secondary: '#64748b',
  success: '#10b981',
  successHover: '#059669',
  warning: '#f59e0b',
  danger: '#ef4444',
  dangerHover: '#dc2626',
  info: '#06b6d4',
  infoHover: '#0891b2',
  light: '#f8fafc',
  lighter: '#f1f5f9',
  dark: '#1e293b',
  border: '#e2e8f0',
  text: '#334155',
  textMuted: '#64748b',
  background: '#f8fafc'
};

// CSS Styles with modern color scheme
const styles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%',
    backgroundColor: colors.background,
    position: 'relative'
  },
  contentWrapper: {
    flexGrow: 1,
    padding: '20px',
    backgroundColor: colors.background,
    transition: 'margin-left 0.3s ease-in-out'
  },
  contentHeader: {
    padding: '15px 0'
  },
  containerFluid: {
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px',
    marginRight: 'auto',
    marginLeft: 'auto'
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: '-15px',
    marginLeft: '-15px'
  },
  col12: {
    flex: '0 0 100%',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px'
  },
  col6: {
    flex: '0 0 50%',
    maxWidth: '50%',
    position: 'relative',
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px'
  },
  mb2: {
    marginBottom: '0.5rem'
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    wordWrap: 'break-word',
    backgroundColor: '#fff',
    backgroundClip: 'border-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    marginBottom: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  cardHeader: {
    padding: '1.25rem 1.5rem',
    marginBottom: 0,
    backgroundColor: colors.lighter,
    borderBottom: `1px solid ${colors.border}`,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  cardTitle: {
    float: 'left',
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
    color: colors.dark
  },
  cardBody: {
    flex: '1 1 auto',
    padding: '1.5rem'
  },
  cardFooter: {
    padding: '1rem 1.5rem',
    backgroundColor: colors.lighter,
    borderTop: `1px solid ${colors.border}`,
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px'
  },
  table: {
    width: '100%',
    marginBottom: '1rem',
    color: colors.text,
    borderCollapse: 'collapse'
  },
  tableBordered: {
    border: `1px solid ${colors.border}`
  },
  tableHover: {
    cursor: 'pointer'
  },
  th: {
    padding: '1rem 0.75rem',
    verticalAlign: 'top',
    borderTop: `1px solid ${colors.border}`,
    borderBottom: `2px solid ${colors.border}`,
    backgroundColor: colors.lighter,
    fontWeight: 600,
    color: colors.dark,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  td: {
    padding: '0.875rem 0.75rem',
    verticalAlign: 'top',
    borderTop: `1px solid ${colors.border}`,
    color: colors.text
  },
  textCenter: {
    textAlign: 'center'
  },
  badge: {
    display: 'inline-block',
    padding: '0.375em 0.75em',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  },
  badgeSuccess: {
    color: '#fff',
    backgroundColor: colors.success
  },
  badgeDanger: {
    color: '#fff',
    backgroundColor: colors.danger
  },
  btn: {
    display: 'inline-block',
    fontWeight: 500,
    textAlign: 'center',
    verticalAlign: 'middle',
    userSelect: 'none',
    border: '1px solid transparent',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    borderRadius: '8px',
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  btnSm: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    borderRadius: '6px'
  },
  btnGroup: {
    position: 'relative',
    display: 'inline-flex',
    verticalAlign: 'middle',
    gap: '0.5rem'
  },
  btnInfo: {
    color: '#fff',
    backgroundColor: colors.info,
    borderColor: colors.info
  },
  btnInfoHover: {
    backgroundColor: colors.infoHover,
    borderColor: colors.infoHover
  },
  btnPrimary: {
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  btnPrimaryHover: {
    backgroundColor: colors.primaryHover,
    borderColor: colors.primaryHover
  },
  btnSuccess: {
    color: '#fff',
    backgroundColor: colors.success,
    borderColor: colors.success
  },
  btnSuccessHover: {
    backgroundColor: colors.successHover,
    borderColor: colors.successHover
  },
  btnDefault: {
    color: colors.text,
    backgroundColor: '#fff',
    borderColor: colors.border
  },
  btnDefaultHover: {
    backgroundColor: colors.lighter,
    borderColor: colors.secondary
  },
  btnDanger: {
    color: '#fff',
    backgroundColor: colors.danger,
    borderColor: colors.danger
  },
  btnDangerHover: {
    backgroundColor: colors.dangerHover,
    borderColor: colors.dangerHover
  },
  ml1: {
    marginLeft: '0.25rem'
  },
  ml2: {
    marginLeft: '0.5rem'
  },
  mr1: {
    marginRight: '0.25rem'
  },
  mr2: {
    marginRight: '0.5rem'
  },
  mt1: {
    marginTop: '0.25rem'
  },
  mt2: {
    marginTop: '0.5rem'
  },
  p4: {
    padding: '1.5rem'
  },
  formControl: {
    display: 'block',
    width: '100%',
    height: 'calc(1.5em + 1rem + 2px)',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: colors.text,
    backgroundColor: '#fff',
    backgroundClip: 'padding-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
  },
  formControlSm: {
    height: 'calc(1.5em + 0.5rem + 2px)',
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    borderRadius: '6px'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  dFlex: {
    display: 'flex'
  },
  justifyContentEnd: {
    justifyContent: 'flex-end'
  },
  alignItemsCenter: {
    alignItems: 'center'
  },
  flex1: {
    flex: 1
  },
  pagination: {
    display: 'flex',
    paddingLeft: 0,
    listStyle: 'none',
    borderRadius: '8px',
    margin: 0
  },
  pageItem: {
    margin: '0 2px'
  },
  pageLink: {
    position: 'relative',
    display: 'block',
    padding: '0.5rem 0.75rem',
    marginLeft: '-1px',
    lineHeight: 1.25,
    color: colors.primary,
    backgroundColor: '#fff',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    borderRadius: '6px'
  },
  disabled: {
    color: colors.textMuted,
    pointerEvents: 'none',
    cursor: 'auto',
    backgroundColor: '#fff',
    borderColor: colors.border,
    opacity: 0.65
  },
  active: {
    zIndex: 1,
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  floatRight: {
    float: 'right'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1050,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    outline: 0,
    display: 'block',
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)'
  },
  modalSecondary: {
    zIndex: 1060
  },
  modalDialog: {
    position: 'relative',
    width: 'auto',
    margin: '0.5rem',
    pointerEvents: 'none',
    maxWidth: '600px',
    margin: '1.75rem auto'
  },
  modalContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    pointerEvents: 'auto',
    backgroundColor: '#fff',
    backgroundClip: 'padding-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    outline: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  modalHeaderPrimary: {
    backgroundColor: colors.primary,
    color: '#fff'
  },
  modalTitle: {
    marginBottom: 0,
    lineHeight: 1.5,
    fontSize: '1.25rem',
    fontWeight: 600
  },
  modalBody: {
    position: 'relative',
    flex: '1 1 auto',
    padding: '1.5rem'
  },
  close: {
    float: 'right',
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1,
    color: '#000',
    textShadow: '0 1px 0 #fff',
    opacity: '.5',
    padding: 0,
    backgroundColor: 'transparent',
    border: 0,
    appearance: 'none',
    cursor: 'pointer'
  },
  closeInModal: {
    color: '#fff',
    textShadow: 'none'
  },
  alert: {
    position: 'relative',
    padding: '0.75rem 1.25rem',
    marginBottom: '1rem',
    border: '1px solid transparent',
    borderRadius: '8px'
  },
  alertDanger: {
    color: '#7f1d1d',
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca'
  },
  textDanger: {
    color: colors.danger
  },
  spinAnimation: {
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' }
    },
    animation: 'spin 1s linear infinite'
  }
};

// Custom Dialog Box Component with better z-index handling
const DialogBox = ({ children, onClose, title, isSecondary = false }) => {
  return (
    <div 
      style={isSecondary ? { ...styles.modal, ...styles.modalSecondary } : styles.modal}
      onClick={onClose}
    >
      <div style={styles.modalDialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalContent}>
          <div style={{ ...styles.modalHeader, ...styles.modalHeaderPrimary }}>
            <h5 style={{ ...styles.modalTitle, color: '#fff' }}>{title}</h5>
            <button
              style={{ ...styles.close, ...styles.closeInModal }}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '0.5'}
            >
              <Icon name="faTimes" />
            </button>
          </div>
          <div style={styles.modalBody}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConnectedDevicesPage = () => {
  const [itemsPerPage] = useState(5);
  const navigate = useNavigate();
  const [showRowJsonPopup, setShowRowJsonPopup] = useState(false);
  const [showTypePopup, setShowTypePopup] = useState(false);
  const [packageJson, setPackageJson] = useState("");
  const [jsonType, setJsonType] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [finalJson, setFinalJson] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activePopupSource, setActivePopupSource] = useState("");
  const [jsonTypes, setJsonTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newTypeName, setNewTypeName] = useState("");
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [editableJson, setEditableJson] = useState("");

  // API data state
  const [devices, setDevices] = useState([]);
  const [fetchingDevices, setFetchingDevices] = useState(true);
  const [deviceError, setDeviceError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return devices.slice(startIndex, endIndex);
  };

  const deviceToSend = selectedDevice || getCurrentItems()[0];
  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch connected devices from API
  const fetchConnectedDevices = async () => {
    setFetchingDevices(true);
    setDeviceError(null);
    try {
      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/Dashboard/SubUser/ConnectedList',
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token
          }
        }
      );

      if (response.data && response.data.isresponse) {
        const deviceList = response.data.list || [];
        setDevices(deviceList);
        setTotalPages(Math.ceil(deviceList.length / itemsPerPage));
      } else {
        setDeviceError(response.data?.errormsg || "Failed to fetch connected devices");
      }
    } catch (err) {
      console.error("Error fetching connected devices:", err);
      setDeviceError(err.message || "An error occurred while fetching devices");
    } finally {
      setFetchingDevices(false);
    }
  };

  const handleAddNewType = async () => {
    setShowAddTypeModal(true);
  };

  const handleSendFinalJson = async () => {
    try {
      // Validate JSON format first
      let jsonToSend;
      try {
        jsonToSend = JSON.parse(editableJson);
      } catch (parseError) {
        alert('Invalid JSON format. Please check your JSON syntax.');
        return;
      }

      // Get selected device data
      const device = selectedDevice;
      if (!device) {
        alert("No device selected.");
        return;
      }

      // Prepare payload
      const payload = {
        merchantname: device.merchantname || device.merchantid || "",
        posname: device.posname || "",
        actionname: jsonType || "Clean",
        id: device.id || device.userid || 0,
        withaction: true,
        msg: editableJson
      };

      console.log("Sending payload:", payload);

      // Send request
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SendToDevice/SubUser/General',
        payload,
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse) {
        alert('JSON sent successfully!');
        setShowTypePopup(false);
      } else {
        alert(`Error: ${response.data?.errormsg || 'Failed to send JSON'}`);
      }
    } catch (err) {
      console.error("Error sending JSON:", err);
      alert(`Error sending JSON: ${err.response?.data?.errormsg || err.message}`);
    }
  };

  const handleSubmitNewType = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/ActionOperation/SubUser/Insert',
        { name: newTypeName },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        await fetchJsonTypes();
        setShowAddTypeModal(false);
        setNewTypeName("");
        alert("Type added successfully!");
      } else {
        throw new Error(response.data.errormsg || "Failed to add type");
      }
    } catch (err) {
      console.error("Error adding new type:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch JSON types from API
  const fetchJsonTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/ActionOperation/SubUser/GetDistinct/name',
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token
          }
        }
      );

      if (response.data.isresponse && response.data.responsestatus === "SUCCESS" && Array.isArray(response.data.data)) {
        setJsonTypes(response.data.data);
        return response.data.data;
      } else {
        setJsonTypes([]);
        if (response.data.errormsg && response.data.errormsg !== "success") {
          setError(response.data.errormsg);
        }
        return [];
      }
    } catch (err) {
      console.error("Error fetching JSON types:", err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle sending JSON for Row JSON popup
  const handleSendJson = async () => {
    try {
      // Validate JSON format first
      let jsonToSend;
      try {
        jsonToSend = JSON.parse(editableJson);
      } catch (parseError) {
        alert('Invalid JSON format. Please check your JSON syntax.');
        return;
      }

      // Get selected device data
      const device = selectedDevice;
      if (!device) {
        alert("No device selected.");
        return;
      }

      // Prepare payload
      const payload = {
        merchantname: device.merchantname || device.merchantid || "",
        posname: device.posname || "",
        actionname: "Clean",
        id: device.id || device.userid || 0,
        withaction: false,
        msg: editableJson
      };

      console.log("Sending payload:", payload);

      // Send request
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SendToDevice/SubUser/General',
        payload,
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse) {
        alert('JSON sent successfully!');
        setShowRowJsonPopup(false);
      } else {
        alert(`Error: ${response.data?.errormsg || 'Failed to send JSON'}`);
      }
    } catch (err) {
      console.error("Error sending JSON:", err);
      alert(`Error sending JSON: ${err.response?.data?.errormsg || err.message}`);
    }
  };

  // Handle pagination
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleSendJsonPackage = (device) => {
    setSelectedDevice(device);
    setActivePopupSource("package");
    setPackageJson("");
    setEditableJson("");
    setShowTypePopup(true);
  };

  const handleRowJsonButtonClick = (device) => {
    setSelectedDevice(device);
    setActivePopupSource("row");
    setEditableJson("");
    setShowRowJsonPopup(true);
  };

  const exampleJsons = {
    row: `{
  "action": "display_message",
  "message": "Hello World!",
  "duration": 5000,
  "priority": "high"
}`,
    package: `{
  "package_name": "my_package",
  "version": "1.0.0"
}`
  };

  // useEffect for fetching JSON types when popup opens
  useEffect(() => {
    if (showTypePopup) {
      fetchJsonTypes();
    }
  }, [showTypePopup]);

  // Fetch devices on component mount
  useEffect(() => {
    fetchConnectedDevices();
    fetchJsonTypes();
  }, []);

  return (
    <div>
      <Menu />

      {/* Content Wrapper */}
      <div className="content-wrapper">
        {/* Content Header (Page header) */}
        <section style={styles.contentHeader}>
          <div style={styles.containerFluid}>
            {/* This row spans full width, with left and right sections */}
            <div style={{...styles.row, ...styles.mb2}}>
              {/* Left Side - Label */}
              <div style={styles.col6}>
                <h1>Connected Devices</h1>
              </div>

              {/* Right Side - Buttons */}
              <div style={{ ...styles.col6, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  style={{ ...styles.btn, ...styles.btnInfo }}
                  onClick={() => navigate('/Send-Row-Json')}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.infoHover;
                    e.target.style.borderColor = colors.infoHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.info;
                    e.target.style.borderColor = colors.info;
                  }}
                >
                  Send Row JSON
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnInfo }}
                  onClick={() => navigate('/Send-json-Package')}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.infoHover;
                    e.target.style.borderColor = colors.infoHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.info;
                    e.target.style.borderColor = colors.info;
                  }}
                >
                  Send JSON Package
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Main content */}
        <section style={{padding: '20px 0'}}>
          <div style={styles.containerFluid}>
            <div style={styles.row}>
              <div style={styles.col12}>
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h3 style={styles.cardTitle}>
                      <Icon name="faTable" /> Connected Devices List
                    </h3>
                  </div>

                  {/* Card Body */}
                  <div style={styles.cardBody}>
                    {fetchingDevices ? (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <Icon name="faSpinner" />
                        <p>Loading devices...</p>
                      </div>
                    ) : deviceError ? (
                      <div style={{ ...styles.alert, ...styles.alertDanger }}>
                        Error loading devices: {deviceError}
                      </div>
                    ) : (
                      <table style={{ ...styles.table, ...styles.tableBordered }}>
                        <thead>
                          <tr>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Userid</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Posname</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Merchantid</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Key</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Active</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Serialno</th>
                            <th style={{ ...styles.th, ...styles.textCenter }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getCurrentItems().length > 0 ? (
                            getCurrentItems().map((device, index) => (
                              <tr key={index}>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.id}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.posname}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.merchantid}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.key}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.active}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>{device.serialno || "-"}</td>
                                <td style={{ ...styles.td, ...styles.textCenter }}>
                                  <button
                                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnInfo, ...styles.mr1 }}
                                    onClick={() => handleRowJsonButtonClick(device)}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = colors.infoHover;
                                      e.target.style.borderColor = colors.infoHover;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = colors.info;
                                      e.target.style.borderColor = colors.info;
                                    }}
                                  >
                                    Send Row JSON
                                  </button>
                                  <button
                                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnPrimary, ...styles.mr1 }}
                                    onClick={() => handleSendJsonPackage(device)}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = colors.primaryHover;
                                      e.target.style.borderColor = colors.primaryHover;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = colors.primary;
                                      e.target.style.borderColor = colors.primary;
                                    }}
                                  >
                                    Send JSON Package
                                  </button>
                                  <select
                                    style={{ ...styles.formControl, ...styles.formControlSm, width: 'auto', display: 'inline-block' }}
                                    onChange={(e) => {
                                      const selectedOption = e.target.value;
                                      const merchantname = device.merchantname || device.merchantid || "";
                                      const posid = device.posname || "";

                                      if (selectedOption === "send_campaign") {
                                        navigate(`/sendcompaign/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_audio") {
                                        navigate(`/sendaudio/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_audio_Package") {
                                        navigate(`/sendaudioPackage/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_catalog") {
                                        navigate(`/send-catelog/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_dqr") {
                                        navigate(`/send-Dqr-image/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_firmware") {
                                        navigate(`/send-firmware/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "send_MQTT_Setting") {
                                        navigate(`/sendMQTTSetting/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption === "sendSerial_noMqtt_Setting") {
                                        navigate(`/sendSerialnoMqttSetting/${merchantname}/${posid}`, {
                                          state: { merchantname, posid }
                                        });
                                      } else if (selectedOption) {
                                        console.log(`Selected option: ${selectedOption}`);
                                      }
                                    }}
                                  >
                                    <option value="">Select Action</option>
                                    <option value="send_campaign">Send Campaign</option>
                                    <option value="send_audio">Send Audio</option>
                                    <option value="send_audio_Package">Send Audio Package</option>
                                    <option value="send_catalog">Send Catalogue</option>
                                    <option value="send_dqr">Send DQR Images</option>
                                    <option value="send_firmware">Send Firmware</option>
                                    <option value="send_json">Send Json</option>
                                    <option value="send_MQTT_Setting">Send MQTTSetting</option>
                                  </select>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" style={{ ...styles.td, ...styles.textCenter }}>
                                No connected devices found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div style={styles.cardFooter}>
                    <ul style={styles.pagination}>
                      <li style={styles.pageItem}>
                        <button
                          style={{
                            ...styles.pageLink,
                            ...(currentPage === 1 ? styles.disabled : {})
                          }}
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                        >
                          <Icon name="faChevronLeft" />
                        </button>
                      </li>
                      <li style={styles.pageItem}>
                        <span style={{ ...styles.pageLink, ...styles.active }}>
                          {currentPage} of {totalPages}
                        </span>
                      </li>
                      <li style={styles.pageItem}>
                        <button
                          style={{
                            ...styles.pageLink,
                            ...(currentPage === totalPages ? styles.disabled : {})
                          }}
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          <Icon name="faChevronRight" />
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
            </div>
          </div>
          </div>
        </section>

        {/* Row JSON Popup */}
        {showRowJsonPopup && (
          <DialogBox onClose={() => setShowRowJsonPopup(false)} title="Send Row JSON">
            <div>
              <div style={styles.formGroup}>
                <label><strong>JSON Data</strong></label>
                <textarea
                  rows="10"
                  style={{...styles.formControl, height: 'calc(1.5em + 1rem + 86px)'}}
                  value={editableJson}
                  onChange={(e) => setEditableJson(e.target.value)}
                  placeholder={exampleJsons.row}
                />
                <small style={{ color: colors.textMuted }}>
                  Enter your JSON data above. See placeholder for example format.
                </small>
              </div>

              <div style={{ ...styles.dFlex, ...styles.justifyContentEnd, gap: '0.5rem' }}>
                <button
                  style={{ ...styles.btn, ...styles.btnSuccess }}
                  onClick={handleSendJson}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.successHover;
                    e.target.style.borderColor = colors.successHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.success;
                    e.target.style.borderColor = colors.success;
                  }}
                >
                  Send JSON
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnDefault }}
                  onClick={() => setShowRowJsonPopup(false)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.lighter;
                    e.target.style.borderColor = colors.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.borderColor = colors.border;
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </DialogBox>
        )}

        {/* Add New Type Modal */}
        {showAddTypeModal && (
          <DialogBox onClose={() => setShowAddTypeModal(false)} title="Add New JSON Type" isSecondary={true}>
            <div>
              <div style={styles.formGroup}>
                <label><strong>New Type Name</strong></label>
                <input
                  type="text"
                  style={styles.formControl}
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="Enter new type name"
                />
              </div>

              <div style={{ ...styles.dFlex, ...styles.justifyContentEnd, gap: '0.5rem' }}>
                <button
                  style={{ ...styles.btn, ...styles.btnSuccess }}
                  onClick={handleSubmitNewType}
                  disabled={loading || !newTypeName.trim()}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = colors.successHover;
                      e.target.style.borderColor = colors.successHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = colors.success;
                      e.target.style.borderColor = colors.success;
                    }
                  }}
                >
                  {loading ? "Adding..." : "Add Type"}
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnDefault }}
                  onClick={() => setShowAddTypeModal(false)}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = colors.lighter;
                      e.target.style.borderColor = colors.secondary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = '#fff';
                      e.target.style.borderColor = colors.border;
                    }
                  }}
                >
                  Cancel
                </button>
              </div>

              {error && <div style={{ ...styles.alert, ...styles.alertDanger }}>{error}</div>}
            </div>
          </DialogBox>
        )}

        {/* JSON Type Selection Popup */}
        {showTypePopup && (
          <DialogBox onClose={() => setShowTypePopup(false)} title="Select JSON Type">
            <div>
              <div style={styles.formGroup}>
                <label><strong>JSON Type</strong></label>
                <select
                  value={jsonType}
                  onChange={(e) => setJsonType(e.target.value)}
                  style={styles.formControl}
                >
                  <option value="">Select type</option>
                  {loading && <option disabled>Loading types...</option>}
                  {!loading && jsonTypes.length > 0 && jsonTypes.map((item, index) => (
                    <option key={index} value={item.value}>
                      {item.value}
                    </option>
                  ))}
                  {!loading && jsonTypes.length === 0 && !error && (
                    <option disabled>No types available</option>
                  )}
                </select>
                {error && <div style={{ ...styles.alert, ...styles.alertDanger }}>Error loading types: {error}</div>}
              </div>

              <button
                style={{ ...styles.btn, ...styles.btnPrimary, marginBottom: '1rem' }}
                onClick={handleAddNewType}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryHover;
                  e.target.style.borderColor = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.borderColor = colors.primary;
                }}
              >
                <Icon name="faPlus" /> Add New Type
              </button>

              <div style={styles.formGroup}>
                <label><strong>JSON Data (Editable)</strong></label>
                <textarea
                  rows="10"
                  style={{...styles.formControl, height: 'calc(1.5em + 1rem + 86px)'}}
                  value={editableJson}
                  onChange={(e) => setEditableJson(e.target.value)}
                  placeholder={activePopupSource === "package" ? exampleJsons.package : exampleJsons.row}
                />
                <small style={{ color: colors.textMuted }}>
                  Enter your JSON data above. See placeholder for example format.
                </small>
              </div>

              <div style={{ ...styles.dFlex, ...styles.justifyContentEnd, gap: '0.5rem' }}>
                <button
                  style={{ ...styles.btn, ...styles.btnSuccess }}
                  onClick={handleSendFinalJson}
                  disabled={!jsonType || !editableJson.trim()}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = colors.successHover;
                      e.target.style.borderColor = colors.successHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = colors.success;
                      e.target.style.borderColor = colors.success;
                    }
                  }}
                >
                  Send JSON
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnDefault }}
                  onClick={() => setShowTypePopup(false)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.lighter;
                    e.target.style.borderColor = colors.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.borderColor = colors.border;
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </DialogBox>
        )}
      </div>

    </div>
  );
};

export default ConnectedDevicesPage;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}